import { createReducer, on } from '@ngrx/store';

import { RequestStatus } from '../../enums/request-status.enum';
import {
  PowerPage,
  PowerPageBlock,
  PowerPageDetail,
  PowerPageManageOptions,
  PowerPagesListMeta,
  PowerPagesListParams,
} from '../models/power-page.model';
import * as PowerPagesActions from './power-pages.actions';

export interface PowerPagesState {
  powerPages: PowerPage[];
  meta: PowerPagesListMeta | null;
  listStatus: RequestStatus;
  deleteStatus: RequestStatus;
  duplicateStatus: RequestStatus;
  error: string | null;
  lastQuery: PowerPagesListParams;

  // Editor state
  currentPowerPage: PowerPageDetail | null;
  options: PowerPageManageOptions | null;
  detailStatus: RequestStatus;
  optionsStatus: RequestStatus;
  saveStatus: RequestStatus;
  saveError: string | null;

  // Content blocks
  blocks: PowerPageBlock[];
  blocksStatus: RequestStatus;
  blockSaveStatus: RequestStatus;
  blockDeleteStatus: RequestStatus;
  blockError: string | null;
}

const initialState: PowerPagesState = {
  powerPages: [],
  meta: null,
  listStatus: RequestStatus.Idle,
  deleteStatus: RequestStatus.Idle,
  duplicateStatus: RequestStatus.Idle,
  error: null,
  lastQuery: {},

  currentPowerPage: null,
  options: null,
  detailStatus: RequestStatus.Idle,
  optionsStatus: RequestStatus.Idle,
  saveStatus: RequestStatus.Idle,
  saveError: null,

  blocks: [],
  blocksStatus: RequestStatus.Idle,
  blockSaveStatus: RequestStatus.Idle,
  blockDeleteStatus: RequestStatus.Idle,
  blockError: null,
};

export const powerPagesReducer = createReducer(
  initialState,
  on(PowerPagesActions.loadPowerPages, (state, query) => ({
    ...state,
    listStatus: RequestStatus.Loading,
    error: null,
    lastQuery: query,
  })),
  on(PowerPagesActions.loadPowerPagesSuccess, (state, { powerPages, meta }) => ({
    ...state,
    powerPages,
    meta,
    listStatus: RequestStatus.Success,
  })),
  on(PowerPagesActions.loadPowerPagesFailure, (state, { error }) => ({
    ...state,
    listStatus: RequestStatus.Error,
    error,
  })),
  on(PowerPagesActions.deletePowerPage, (state) => ({
    ...state,
    deleteStatus: RequestStatus.Loading,
    error: null,
  })),
  on(PowerPagesActions.deletePowerPageSuccess, (state) => ({
    ...state,
    deleteStatus: RequestStatus.Success,
  })),
  on(PowerPagesActions.duplicatePowerPage, (state) => ({
    ...state,
    duplicateStatus: RequestStatus.Loading,
    error: null,
  })),
  on(PowerPagesActions.duplicatePowerPageSuccess, (state) => ({
    ...state,
    duplicateStatus: RequestStatus.Success,
  })),
  on(PowerPagesActions.mutationFailure, (state, { error }) => ({
    ...state,
    deleteStatus: state.deleteStatus === RequestStatus.Loading ? RequestStatus.Error : state.deleteStatus,
    duplicateStatus:
      state.duplicateStatus === RequestStatus.Loading ? RequestStatus.Error : state.duplicateStatus,
    error,
  })),
  on(PowerPagesActions.resetPowerPagesState, () => initialState),

  // Options
  on(PowerPagesActions.loadPowerPageOptions, (state) => ({
    ...state,
    optionsStatus: RequestStatus.Loading,
  })),
  on(PowerPagesActions.loadPowerPageOptionsSuccess, (state, { options }) => ({
    ...state,
    options,
    optionsStatus: RequestStatus.Success,
  })),
  on(PowerPagesActions.loadPowerPageOptionsFailure, (state, { error }) => ({
    ...state,
    optionsStatus: RequestStatus.Error,
    error,
  })),

  // Single load
  on(PowerPagesActions.loadPowerPage, (state) => ({
    ...state,
    currentPowerPage: null,
    detailStatus: RequestStatus.Loading,
    error: null,
  })),
  on(PowerPagesActions.loadPowerPageSuccess, (state, { powerPage }) => ({
    ...state,
    currentPowerPage: powerPage,
    detailStatus: RequestStatus.Success,
  })),
  on(PowerPagesActions.loadPowerPageFailure, (state, { error }) => ({
    ...state,
    detailStatus: RequestStatus.Error,
    error,
  })),

  // Save (create/update)
  on(PowerPagesActions.savePowerPage, (state) => ({
    ...state,
    saveStatus: RequestStatus.Loading,
    saveError: null,
  })),
  on(PowerPagesActions.savePowerPageSuccess, (state, { powerPage }) => ({
    ...state,
    saveStatus: RequestStatus.Success,
    currentPowerPage: powerPage,
  })),
  on(PowerPagesActions.savePowerPageFailure, (state, { error }) => ({
    ...state,
    saveStatus: RequestStatus.Error,
    saveError: error,
  })),

  on(PowerPagesActions.resetPowerPageEditor, (state) => ({
    ...state,
    currentPowerPage: null,
    detailStatus: RequestStatus.Idle,
    saveStatus: RequestStatus.Idle,
    saveError: null,
    blocks: [],
    blocksStatus: RequestStatus.Idle,
    blockSaveStatus: RequestStatus.Idle,
    blockDeleteStatus: RequestStatus.Idle,
    blockError: null,
  })),

  // Content blocks
  on(PowerPagesActions.loadBlocks, (state) => ({
    ...state,
    blocksStatus: RequestStatus.Loading,
    blockError: null,
  })),
  on(PowerPagesActions.loadBlocksSuccess, (state, { blocks }) => ({
    ...state,
    blocks,
    blocksStatus: RequestStatus.Success,
  })),
  on(PowerPagesActions.loadBlocksFailure, (state, { error }) => ({
    ...state,
    blocksStatus: RequestStatus.Error,
    blockError: error,
  })),

  on(PowerPagesActions.createBlock, PowerPagesActions.editBlock, (state) => ({
    ...state,
    blockSaveStatus: RequestStatus.Loading,
    blockError: null,
  })),
  on(PowerPagesActions.saveBlockSuccess, (state) => ({
    ...state,
    blockSaveStatus: RequestStatus.Success,
  })),
  on(PowerPagesActions.saveBlockFailure, (state, { error }) => ({
    ...state,
    blockSaveStatus: RequestStatus.Error,
    blockError: error,
  })),

  on(PowerPagesActions.deleteBlock, (state) => ({
    ...state,
    blockDeleteStatus: RequestStatus.Loading,
    blockError: null,
  })),
  on(PowerPagesActions.deleteBlockSuccess, (state) => ({
    ...state,
    blockDeleteStatus: RequestStatus.Success,
  })),
  on(PowerPagesActions.deleteBlockFailure, (state, { error }) => ({
    ...state,
    blockDeleteStatus: RequestStatus.Error,
    blockError: error,
  })),

  on(PowerPagesActions.resetBlocks, (state) => ({
    ...state,
    blocks: [],
    blocksStatus: RequestStatus.Idle,
    blockSaveStatus: RequestStatus.Idle,
    blockDeleteStatus: RequestStatus.Idle,
    blockError: null,
  }))
);
